import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "@/api/axiosInstance";
import { type AdminColor } from "./useAdmin";

// Colors CRUD hooks
export const useColors = (enabled: boolean = true) => {
    return useQuery<AdminColor[]>({
        queryKey: ['admin', 'colors'],
        queryFn: async () => {
            const { data } = await axiosInstance.get('/products/colors/');
            return data.results || data;
        },
        enabled,
    });
};

export const useColor = (id: number, enabled: boolean = true) => {
    return useQuery<AdminColor>({
        queryKey: ['admin', 'colors', id],
        queryFn: async () => {
            const { data } = await axiosInstance.get(`/products/colors/${id}/`);
            return data;
        },
        enabled: enabled && !!id,
    });
};

export const useCreateColor = () => {
    const queryClient = useQueryClient();

    return useMutation<AdminColor, Error, Omit<AdminColor, 'id' | 'created_at' | 'updated_at'>>({
        mutationFn: async (colorData) => {
            const { data } = await axiosInstance.post('/products/colors/', colorData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'colors'] });
        },
    });
};

export const useUpdateColor = () => {
    const queryClient = useQueryClient();

    return useMutation<AdminColor, Error, { id: number; data: Partial<Omit<AdminColor, 'id' | 'created_at' | 'updated_at'>> }>({
        mutationFn: async ({ id, data }) => {
            const { data: response } = await axiosInstance.patch(`/products/colors/${id}/`, data);
            return response;
        },
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'colors'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'colors', id] });
        },
    });
};

export const useDeleteColor = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, number>({
        mutationFn: async (id: number) => {
            await axiosInstance.delete(`/products/colors/${id}/`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'colors'] });
        },
    });
};
